import { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react'

import { useGenerateArticleRewriteStream } from '@/common/api/modules/articleDiagnosis'
import { ArticleRewriteResult } from '@/common/api/params/articleDiagnosis'
import { ArticleProposalDetail, ArticleTone } from '@/common/model/media/article'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import CheckBox, { CheckBoxColor } from '@/components/view/atoms/checkbox/CheckBox'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Triangle from '@/components/view/atoms/icon/Triangle'
import AiLoading from '@/components/view/atoms/loading/AiLoading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextArea from '@/components/view/atoms/textarea/Textarea'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Line from '@/components/view/layouts/area/Line'
import AiLoadingArea from '@/components/view/layouts/loading/AiLoadingArea'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'
import RewriteProgressStepper from '@/components/view/molecules/progress/RewriteProgressStepper'

import {
  AppliedArticleRewriteFields,
  ArticleRewriteSelection,
  EMPTY_REWRITE_SELECTION,
  buildAppliedRewriteFields,
  hasAnySelection
} from '@/components/container/modal/article/articleRewriteApply'

import { ArticleRewriteModalContext } from './ArticleRewriteModalProvider'

const SectionTitle = ({ children }: { children: string }) => (
  <Text size={14} weight='bold'>
    {children}
  </Text>
)

const AiReasonChip = ({ children }: { children: string }) => (
  <div className='bg-surface flex items-start gap-1 rounded-md px-4 py-2'>
    <span className='shrink-0 pt-px'>
      <Icon type={IconType.AI_FILL} size={12} />
    </span>
    <Text size={12} color={TextColor.AI}>
      {children}
    </Text>
  </div>
)

const AiRewriteCard = ({
  name,
  label,
  reason,
  checked,
  onChange,
  onEdit,
  children,
  contentAdding
}: {
  name: string
  label: string
  reason?: string | null
  checked: boolean
  onChange(checked: boolean): void
  onEdit?(): void
  children: ReactNode
  contentAdding?: boolean
}) => (
  <div className='flex items-center gap-1'>
    {contentAdding ? (
      <Icon type={IconType.PLUS} color={IconColor.PURPLE} size={24} />
    ) : (
      <Triangle />
    )}
    <div className='bg-fill-ai-secondary relative w-full rounded-lg p-1'>
      <div className='flex flex-col gap-1 px-1'>
        <div className='flex items-center justify-between gap-2'>
          <Text size={12} weight='bold' color={TextColor.AI}>
            {label}
          </Text>
          <CheckBox
            name={name}
            label='反映に取り入れる'
            checked={checked}
            onChange={onChange}
            nowrap
            color={CheckBoxColor.AI}
          />
        </div>
        <div className='flex items-start justify-between gap-1'>
          <div className='min-w-0 flex-1'>{children}</div>
          {onEdit && (
            <span className='shrink-0'>
              <IconButton type={IconType.EDIT} line onClick={onEdit} />
            </span>
          )}
        </div>
        {reason && <AiReasonChip>{reason}</AiReasonChip>}
      </div>
    </div>
  </div>
)

// 「AI案を修正」カード。編集ボタン押下時に表示し、初期値には編集元リライト案のテキストが入る。
// 「反映に取り入れる」をチェックしていれば、AI案ではなく修正後のテキストを採用する。
const AiRewriteEditCard = ({
  name,
  text,
  adopt,
  onChangeText,
  onChangeAdopt
}: {
  name: string
  text: string
  adopt: boolean
  onChangeText(text: string): void
  onChangeAdopt(adopt: boolean): void
}) => (
  <div className='flex items-center gap-0.5'>
    <Triangle />
    <div className='bg-ruri-400 relative w-full rounded-lg p-1'>
      <div className='flex flex-col gap-1 px-1'>
        <div className='flex items-center justify-between gap-2'>
          <Text size={12} weight='bold' color={TextColor.AI}>
            AI案を修正
          </Text>
          <CheckBox
            name={name}
            label='反映に取り入れる'
            checked={adopt}
            onChange={onChangeAdopt}
            nowrap
          />
        </div>
        <TextArea
          minRows={2}
          value={text}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChangeText(e.target.value)}
        />
      </div>
    </div>
  </div>
)

const BeforeBox = ({ children }: { children: ReactNode }) => (
  <div className='bg-surface-secondary flex h-full flex-col gap-2 rounded-lg p-4'>{children}</div>
)

// 案の反映先（コンテンツエリア上部／下部）。target_area が無い案は上部へ反映される
const TargetAreaLabel = ({ targetArea }: { targetArea?: string }) => (
  <Text size={12} color={TextColor.SECONDARY}>
    反映先: {targetArea === 'content_area_bottom' ? 'コンテンツエリア下部' : 'コンテンツエリア上部'}
  </Text>
)

const RewriteRow = ({ before, after }: { before: ReactNode; after: ReactNode }) => (
  <div className='grid grid-cols-1 gap-1 md:grid-cols-2 md:items-start md:gap-2'>
    {before}
    {after}
  </div>
)

interface ResultBodyProps {
  detail: ArticleProposalDetail
  rewrite: ArticleRewriteResult
  selection: ArticleRewriteSelection
  onSelectionChange(selection: ArticleRewriteSelection): void
}

const ArticleRewriteResultBody = ({
  detail,
  rewrite,
  selection,
  onSelectionChange
}: ResultBodyProps) => {
  if (rewrite.error) {
    return (
      <Text size={14} color={TextColor.CRITICAL}>
        {rewrite.error}
      </Text>
    )
  }

  const titleRewrites = rewrite.title_rewrites ?? []
  const metaRewrites = rewrite.meta_description_rewrites ?? []
  const headingRewrites = rewrite.heading_rewrites ?? []
  const newSections = rewrite.new_sections ?? []
  const copyImprovements = rewrite.copy_improvements ?? []

  type RadioIndexKey = 'titleIndex' | 'metaIndex'
  type RadioEditKey = 'titleEdit' | 'metaEdit'
  type SetIndexKey = 'headingIndexes' | 'sectionIndexes' | 'copyIndexes'
  type SetEditKey = 'headingEdits' | 'sectionEdits' | 'copyEdits'

  // 候補選択（タイトル/メタ = 単一選択）。同グループの手動修正の採用とは排他。
  const toggleRadio = (indexKey: RadioIndexKey, editKey: RadioEditKey, idx: number) => {
    const edit = selection[editKey]
    onSelectionChange({
      ...selection,
      [indexKey]: selection[indexKey] === idx ? null : idx,
      [editKey]: edit ? { ...edit, adopt: false } : edit
    })
  }

  // 候補選択（見出し/追加/コピー = 複数選択）。同じ案の手動修正の採用とは排他。
  const toggleSet = (setKey: SetIndexKey, editKey: SetEditKey, idx: number) => {
    const next = new Set(selection[setKey])
    const edits = new Map(selection[editKey])
    if (next.has(idx)) {
      next.delete(idx)
    } else {
      next.add(idx)
      const edit = edits.get(idx)
      if (edit) edits.set(idx, { ...edit, adopt: false })
    }
    onSelectionChange({ ...selection, [setKey]: next, [editKey]: edits })
  }

  // 「AI案を修正」を表示（編集開始）。初期値に編集元リライト案のテキストを入れ、採用ON・候補選択は解除。
  const startRadioEdit = (indexKey: RadioIndexKey, editKey: RadioEditKey, text: string) =>
    onSelectionChange({ ...selection, [indexKey]: null, [editKey]: { text, adopt: true } })

  const changeRadioEditText = (editKey: RadioEditKey, text: string) => {
    const edit = selection[editKey]
    if (!edit) return
    onSelectionChange({ ...selection, [editKey]: { ...edit, text } })
  }

  const changeRadioEditAdopt = (indexKey: RadioIndexKey, editKey: RadioEditKey, adopt: boolean) => {
    const edit = selection[editKey]
    if (!edit) return
    onSelectionChange({
      ...selection,
      [editKey]: { ...edit, adopt },
      ...(adopt ? { [indexKey]: null } : {})
    })
  }

  const startSetEdit = (setKey: SetIndexKey, editKey: SetEditKey, idx: number, text: string) => {
    const next = new Set(selection[setKey])
    next.delete(idx)
    const edits = new Map(selection[editKey])
    edits.set(idx, { text, adopt: true })
    onSelectionChange({ ...selection, [setKey]: next, [editKey]: edits })
  }

  const changeSetEditText = (editKey: SetEditKey, idx: number, text: string) => {
    const edit = selection[editKey].get(idx)
    if (!edit) return
    const edits = new Map(selection[editKey])
    edits.set(idx, { ...edit, text })
    onSelectionChange({ ...selection, [editKey]: edits })
  }

  const changeSetEditAdopt = (
    setKey: SetIndexKey,
    editKey: SetEditKey,
    idx: number,
    adopt: boolean
  ) => {
    const edit = selection[editKey].get(idx)
    if (!edit) return
    const edits = new Map(selection[editKey])
    edits.set(idx, { ...edit, adopt })
    const next = new Set(selection[setKey])
    if (adopt) next.delete(idx)
    onSelectionChange({ ...selection, [editKey]: edits, [setKey]: next })
  }

  return (
    <div className='flex flex-col gap-2'>
      {titleRewrites.length > 0 && (
        <>
          <section className='flex flex-col gap-3 p-6'>
            <SectionTitle>タイトル</SectionTitle>
            <RewriteRow
              before={
                <BeforeBox>
                  <Text size={16} weight='bold'>
                    {titleRewrites[0].original ||
                      detail.latest_article.title ||
                      '（現在のタイトル）'}
                  </Text>
                </BeforeBox>
              }
              after={
                <div className='flex flex-col gap-2'>
                  {titleRewrites.map((item, idx) => (
                    <AiRewriteCard
                      key={idx}
                      name={`rewrite-title-${idx}`}
                      label={
                        titleRewrites.length > 1
                          ? `リライト ${String.fromCharCode(65 + idx)}`
                          : 'リライト'
                      }
                      reason={item.reason}
                      checked={selection.titleIndex === idx}
                      onChange={() => toggleRadio('titleIndex', 'titleEdit', idx)}
                      onEdit={() => startRadioEdit('titleIndex', 'titleEdit', item.rewritten)}
                    >
                      <Text size={16} weight='bold'>
                        {item.rewritten}
                      </Text>
                      {item.seo_keywords && item.seo_keywords.length > 0 && (
                        <div className='mt-2 flex flex-wrap gap-1.5'>
                          {item.seo_keywords.map((kw, ki) => (
                            <Badge key={ki} size={10} badgeType={BadgeType.SECONDARY}>
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </AiRewriteCard>
                  ))}
                  {selection.titleEdit && (
                    <AiRewriteEditCard
                      name='rewrite-title-edit'
                      text={selection.titleEdit.text}
                      adopt={selection.titleEdit.adopt}
                      onChangeText={(text) => changeRadioEditText('titleEdit', text)}
                      onChangeAdopt={(adopt) =>
                        changeRadioEditAdopt('titleIndex', 'titleEdit', adopt)
                      }
                    />
                  )}
                </div>
              }
            />
          </section>
          <Line />
        </>
      )}

      {metaRewrites.length > 0 && (
        <>
          <section className='flex flex-col gap-3 p-6'>
            <SectionTitle>メタディスクリプション</SectionTitle>
            <RewriteRow
              before={
                <BeforeBox>
                  <Text size={14} lineHeight={150}>
                    {metaRewrites[0].original || detail.latest_article.meta_description || '—'}
                  </Text>
                </BeforeBox>
              }
              after={
                <div className='flex flex-col gap-2'>
                  {metaRewrites.map((item, idx) => (
                    <AiRewriteCard
                      key={idx}
                      name={`rewrite-meta-${idx}`}
                      label={
                        metaRewrites.length > 1
                          ? `リライト ${String.fromCharCode(65 + idx)}`
                          : 'リライト'
                      }
                      reason={item.reason}
                      checked={selection.metaIndex === idx}
                      onChange={() => toggleRadio('metaIndex', 'metaEdit', idx)}
                      onEdit={() => startRadioEdit('metaIndex', 'metaEdit', item.rewritten)}
                    >
                      <Text size={14} lineHeight={150}>
                        {item.rewritten}
                      </Text>
                      <div className='mt-1'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {item.rewritten.length}文字
                        </Text>
                      </div>
                    </AiRewriteCard>
                  ))}
                  {selection.metaEdit && (
                    <AiRewriteEditCard
                      name='rewrite-meta-edit'
                      text={selection.metaEdit.text}
                      adopt={selection.metaEdit.adopt}
                      onChangeText={(text) => changeRadioEditText('metaEdit', text)}
                      onChangeAdopt={(adopt) =>
                        changeRadioEditAdopt('metaIndex', 'metaEdit', adopt)
                      }
                    />
                  )}
                </div>
              }
            />
          </section>
          <Line />
        </>
      )}

      {(headingRewrites.length > 0 || newSections.length > 0) && (
        <>
          <section className='flex flex-col gap-4 p-6'>
            <SectionTitle>コンテンツ</SectionTitle>
            {headingRewrites.map((item, idx) => (
              <RewriteRow
                key={`h-${idx}`}
                before={
                  <BeforeBox>
                    <TargetAreaLabel targetArea={item.target_area} />
                    <Text size={16} weight='bold'>
                      {item.original || '—'}
                    </Text>
                  </BeforeBox>
                }
                after={
                  <div className='flex flex-col gap-2'>
                    <AiRewriteCard
                      name={`rewrite-heading-${idx}`}
                      label='リライト'
                      reason={item.reason}
                      checked={selection.headingIndexes.has(idx)}
                      onChange={() => toggleSet('headingIndexes', 'headingEdits', idx)}
                      onEdit={() =>
                        startSetEdit('headingIndexes', 'headingEdits', idx, item.rewritten)
                      }
                    >
                      <Text size={16} weight='bold'>
                        {item.rewritten}
                      </Text>
                    </AiRewriteCard>
                    {selection.headingEdits.get(idx) && (
                      <AiRewriteEditCard
                        name={`rewrite-heading-edit-${idx}`}
                        text={selection.headingEdits.get(idx)!.text}
                        adopt={selection.headingEdits.get(idx)!.adopt}
                        onChangeText={(text) => changeSetEditText('headingEdits', idx, text)}
                        onChangeAdopt={(adopt) =>
                          changeSetEditAdopt('headingIndexes', 'headingEdits', idx, adopt)
                        }
                      />
                    )}
                  </div>
                }
              />
            ))}
            {newSections.map((item, idx) => (
              <div key={`n-${idx}`} className='flex flex-col gap-2'>
                <AiRewriteCard
                  name={`rewrite-section-${idx}`}
                  label='コンテンツ追加'
                  reason={item.reason}
                  checked={selection.sectionIndexes.has(idx)}
                  onChange={() => toggleSet('sectionIndexes', 'sectionEdits', idx)}
                  onEdit={() =>
                    startSetEdit('sectionIndexes', 'sectionEdits', idx, item.heading ?? '')
                  }
                  contentAdding
                >
                  <div className='flex items-center gap-2'>
                    {item.level && (
                      <Badge size={12} badgeType={BadgeType.SQUARE_AI} fixed>
                        {item.level}
                      </Badge>
                    )}
                    <Text size={16} weight='bold'>
                      {item.heading}
                    </Text>
                  </div>
                  <div className='mt-1'>
                    <TargetAreaLabel targetArea={item.target_area} />
                  </div>
                  {item.placement && (
                    <div className='mt-1'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        挿入位置: {item.placement}
                      </Text>
                    </div>
                  )}
                </AiRewriteCard>
                {selection.sectionEdits.get(idx) && (
                  <AiRewriteEditCard
                    name={`rewrite-section-edit-${idx}`}
                    text={selection.sectionEdits.get(idx)!.text}
                    adopt={selection.sectionEdits.get(idx)!.adopt}
                    onChangeText={(text) => changeSetEditText('sectionEdits', idx, text)}
                    onChangeAdopt={(adopt) =>
                      changeSetEditAdopt('sectionIndexes', 'sectionEdits', idx, adopt)
                    }
                  />
                )}
              </div>
            ))}
          </section>
          <Line />
        </>
      )}

      {copyImprovements.length > 0 && (
        <section className='flex flex-col gap-4 p-6'>
          <SectionTitle>コピー改善案</SectionTitle>
          {copyImprovements.map((item, idx) => (
            <RewriteRow
              key={idx}
              before={
                <BeforeBox>
                  <TargetAreaLabel targetArea={item.target_area} />
                  {item.area && (
                    <Badge size={12} badgeType={BadgeType.SQUARE_SECONDARY} preWrap>
                      {item.area}
                    </Badge>
                  )}
                  {item.before_hint && (
                    <Text size={14} color={TextColor.CAUTION}>
                      {item.before_hint}
                    </Text>
                  )}
                </BeforeBox>
              }
              after={
                <div className='flex flex-col gap-2'>
                  <AiRewriteCard
                    name={`rewrite-copy-${idx}`}
                    label='改善案'
                    reason={item.reason}
                    checked={selection.copyIndexes.has(idx)}
                    onChange={() => toggleSet('copyIndexes', 'copyEdits', idx)}
                    onEdit={() =>
                      startSetEdit('copyIndexes', 'copyEdits', idx, item.after_example ?? '')
                    }
                  >
                    <Text size={14} lineHeight={150}>
                      {item.after_example || ''}
                    </Text>
                  </AiRewriteCard>
                  {selection.copyEdits.get(idx) && (
                    <AiRewriteEditCard
                      name={`rewrite-copy-edit-${idx}`}
                      text={selection.copyEdits.get(idx)!.text}
                      adopt={selection.copyEdits.get(idx)!.adopt}
                      onChangeText={(text) => changeSetEditText('copyEdits', idx, text)}
                      onChangeAdopt={(adopt) =>
                        changeSetEditAdopt('copyIndexes', 'copyEdits', idx, adopt)
                      }
                    />
                  )}
                </div>
              }
            />
          ))}
        </section>
      )}
    </div>
  )
}

interface ContentProps {
  siteId: number
  detail: ArticleProposalDetail
  tone?: ArticleTone
  persona?: string
  onApply?: (fields: AppliedArticleRewriteFields) => void
  onClose(): void
}

// モーダルが開くたびに再マウントし、AIリライト案の生成を開始する
const ArticleRewriteModalContent = ({
  siteId,
  detail,
  tone,
  persona,
  onApply,
  onClose
}: ContentProps) => {
  const [instruction, setInstruction] = useState('')
  const [selection, setSelection] = useState<ArticleRewriteSelection>(EMPTY_REWRITE_SELECTION)

  const { rewriting, progress, result, error, start } = useGenerateArticleRewriteStream()

  useEffect(() => {
    start({
      site_id: siteId,
      article_proposal_id: detail.article_proposal_id,
      tone,
      persona
    }).catch(() => undefined)
    // マウント時に一度だけ生成を開始する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRegenerate = () => {
    setSelection(EMPTY_REWRITE_SELECTION)
    start({
      site_id: siteId,
      article_proposal_id: detail.article_proposal_id,
      instruction: instruction.trim() || undefined,
      tone,
      persona
    }).catch(() => undefined)
  }

  const handleApply = () => {
    if (!result?.rewrite) return
    onApply?.(buildAppliedRewriteFields(detail, result.rewrite, selection))
    onClose()
  }

  return (
    <>
      <ModalContent type={ModalContentType.NO_FRAME} maxKeep>
        <div className='flex items-end gap-2 p-6 pb-0'>
          <TextArea
            minRows={1}
            value={instruction}
            placeholder='例：もっと技術者向けの専門的な内容にリライトしてください'
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInstruction(e.target.value)}
          />
          <div className='shrink-0'>
            <AiButton size='large' onClick={handleRegenerate} loading={rewriting} nowrap>
              再リライトする
            </AiButton>
          </div>
        </div>

        {error ? (
          <AreaContent>
            <Text size={14} color={TextColor.CRITICAL}>
              {error}
            </Text>
          </AreaContent>
        ) : result ? (
          progress ? (
            <div className='relative'>
              <ArticleRewriteResultBody
                detail={detail}
                rewrite={result.rewrite}
                selection={selection}
                onSelectionChange={setSelection}
              />
              <AiLoadingArea type='transparent'>
                <AiLoading
                  description={progress.message}
                  prompt={`${progress.percent}% : ${progress.detail}`}
                />
              </AiLoadingArea>
            </div>
          ) : (
            <ArticleRewriteResultBody
              detail={detail}
              rewrite={result.rewrite}
              selection={selection}
              onSelectionChange={setSelection}
            />
          )
        ) : (
          <>
            <div className='mt-4 h-[calc(100%-72px)]'>
              <RewriteProgressStepper
                progress={
                  progress ?? {
                    step: 0,
                    total_steps: 5,
                    percent: 0,
                    message: ' AIがリライト案を生成中です。しばらくお待ちください...',
                    detail: ''
                  }
                }
              />
            </div>
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onClose}>
          閉じる
        </Button>
        <Button
          color={ButtonColor.PRIMARY}
          size='large'
          disabled={!result?.rewrite || !hasAnySelection(selection)}
          onClick={handleApply}
        >
          リライトを反映する
        </Button>
      </ModalFooter>
    </>
  )
}

const ArticleRewriteModal = () => {
  const { modal, showing, hide } = useContext(ArticleRewriteModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='AIリライト' onClickClose={hide} />
      {modal.showing && modal.detail && (
        <ArticleRewriteModalContent
          siteId={modal.siteId}
          detail={modal.detail}
          tone={modal.tone}
          persona={modal.persona}
          onApply={modal.onApply}
          onClose={hide}
        />
      )}
    </Modal>
  )
}

export default ArticleRewriteModal
